import type { Http } from "../live/http";
import type { ShareLinkResult } from "../share-types";
import { openedSchema, packageOf, parseCreated, parseLink, parseLinks } from "./schemas";
import { splitLinkToken } from "./token";
import type { ShareLinksApi } from "./types";

/** The share-link routes of the identity service (`crates/services/identity/src/routes/mod.rs`). */
export const LINK_ROUTES = {
  open: "/links/open",
  drawing: (project: string, drawing: string) =>
    `/projects/${encodeURIComponent(project)}/drawings/${encodeURIComponent(drawing)}/links`,
  link: (project: string, link: string, action: "revoke" | "extend" | "events") =>
    `/projects/${encodeURIComponent(project)}/links/${encodeURIComponent(link)}/${action}`,
} as const;

/** Making and managing links, for a project's Editors and up. */
export function createLiveShareLinksApi(http: Http): ShareLinksApi {
  return {
    list: (project, drawing) =>
      http.get(LINK_ROUTES.drawing(project, drawing), { parse: parseLinks }),
    create: (project, drawing, input) =>
      http.send("POST", LINK_ROUTES.drawing(project, drawing), input, { parse: parseCreated }),
    revoke: (project, link) =>
      http.send("POST", LINK_ROUTES.link(project, link, "revoke"), {}, { parse: parseLink }),
    extend: (project, link, days) =>
      http.send(
        "POST",
        LINK_ROUTES.link(project, link, "extend"),
        days === undefined ? {} : { days },
        {
          parse: parseLink,
        },
      ),
  };
}

/**
 * Opening a link, for a visitor without an account: the token is `<link id>#<secret>` (`./token`), posted once in the
 * body. Opening sets the link-session cookie the drawing's sync socket then carries.
 */
export function openLiveShareLink(
  http: Http,
  token: string,
  password?: string,
): Promise<ShareLinkResult> {
  const { link, secret } = splitLinkToken(token);
  if (secret === "") return Promise.resolve({ status: "notFound" });
  const body = password === undefined ? { link, secret } : { link, secret, password };
  return http.send("POST", LINK_ROUTES.open, body, {
    parse: (value): ShareLinkResult => {
      const opened = openedSchema.parse(value);
      return opened.status === "ok" ? { status: "ok", package: packageOf(opened.package) } : opened;
    },
  });
}
